'use client'

import { useEffect, useRef, useState } from 'react'
import { toast } from 'react-toastify'
import {
    MdAdd,
    MdEdit,
    MdDelete,
    MdList,
    MdPlaylistAdd,
} from 'react-icons/md'
import { useAuth } from '@/store/hooks'
import { useResourcePermissions } from '@/lib/hooks/use-resource-permissions'
import ConfirmModal from '@/components/confirm-modal'
import {
    getRubrics,
    createRubric,
    updateRubric,
    deleteRubric,
    seedRubrics,
    HrRubric,
} from '@/lib/api/hr/hr-rubric-api'

const TYPE_LABELS: Record<string, string> = {
    EARNING: 'Provento',
    DEDUCTION: 'Desconto',
    INFORMATIONAL: 'Informativo',
}

const TYPE_BADGES: Record<string, string> = {
    EARNING: 'badge-success',
    DEDUCTION: 'badge-error',
    INFORMATIONAL: 'badge-info',
}

const CALC_TYPE_LABELS: Record<string, string> = {
    FIXED: 'Fixo',
    PERCENTAGE: 'Percentual',
    FORMULA: 'Fórmula',
    TABLE: 'Tabela',
}

interface RubricForm {
    code: string
    name: string
    type: string
    calculationType: string
    defaultValue: string
    formula: string
    incidenceInss: boolean
    incidenceIrrf: boolean
    incidenceFgts: boolean
    incidenceVacation13: boolean
    active: boolean
}

const initialForm: RubricForm = {
    code: '',
    name: '',
    type: 'EARNING',
    calculationType: 'FIXED',
    defaultValue: '',
    formula: '',
    incidenceInss: false,
    incidenceIrrf: false,
    incidenceFgts: false,
    incidenceVacation13: false,
    active: true,
}

export default function RubricsPage() {
    const { user } = useAuth()
    const { canCreate, canUpdate, canDelete } = useResourcePermissions('hr', 'rubrics')

    const [rubrics, setRubrics] = useState<HrRubric[]>([])
    const [loading, setLoading] = useState(true)
    const [seeding, setSeeding] = useState(false)

    // Modal
    const modalRef = useRef<HTMLDialogElement>(null)
    const [editingId, setEditingId] = useState<number | null>(null)
    const [editingSystem, setEditingSystem] = useState(false)
    const [form, setForm] = useState<RubricForm>(initialForm)
    const [saving, setSaving] = useState(false)

    // Delete
    const [deleteId, setDeleteId] = useState<number | null>(null)
    const [deleting, setDeleting] = useState(false)

    useEffect(() => {
        loadRubrics()
    }, [])

    const loadRubrics = async () => {
        setLoading(true)
        try {
            const res = await getRubrics()
            if (res.success && res.result) {
                setRubrics(res.result)
            } else {
                toast.error(res.error || 'Erro ao carregar rubricas')
            }
        } catch {
            toast.error('Erro ao carregar rubricas')
        } finally {
            setLoading(false)
        }
    }

    const handleSeed = async () => {
        setSeeding(true)
        try {
            const res = await seedRubrics()
            if (res.success) {
                toast.success('Rubricas padrão criadas com sucesso')
                loadRubrics()
            } else {
                toast.error(res.error || 'Erro ao criar rubricas padrão')
            }
        } catch {
            toast.error('Erro ao criar rubricas padrão')
        } finally {
            setSeeding(false)
        }
    }

    const openCreateModal = () => {
        setEditingId(null)
        setEditingSystem(false)
        setForm(initialForm)
        modalRef.current?.showModal()
    }

    const openEditModal = (rubric: HrRubric) => {
        setEditingId(rubric.id)
        setEditingSystem(rubric.isSystem)
        const incidence = rubric.incidence || {}
        setForm({
            code: rubric.code,
            name: rubric.name,
            type: rubric.type,
            calculationType: rubric.calculationType,
            defaultValue: rubric.defaultValue != null ? String(rubric.defaultValue) : '',
            formula: rubric.formula || '',
            incidenceInss: !!incidence.inss,
            incidenceIrrf: !!incidence.irrf,
            incidenceFgts: !!incidence.fgts,
            incidenceVacation13: !!incidence.vacation13,
            active: rubric.active,
        })
        modalRef.current?.showModal()
    }

    const closeModal = () => {
        modalRef.current?.close()
        setEditingId(null)
        setEditingSystem(false)
        setForm(initialForm)
    }

    const handleSave = async () => {
        if (!form.code.trim() || !form.name.trim()) {
            toast.warning('Informe o código e o nome da rubrica')
            return
        }

        setSaving(true)
        try {
            const data: any = {
                code: form.code.trim(),
                name: form.name.trim(),
                type: form.type,
                calculationType: form.calculationType,
                defaultValue: form.defaultValue ? parseFloat(form.defaultValue) : null,
                formula: form.formula.trim() || null,
                incidence: {
                    inss: form.incidenceInss,
                    irrf: form.incidenceIrrf,
                    fgts: form.incidenceFgts,
                    vacation13: form.incidenceVacation13,
                },
                active: form.active,
            }

            if (editingId) {
                const res = await updateRubric(editingId, data)
                if (res.success) {
                    toast.success('Rubrica atualizada com sucesso')
                    closeModal()
                    loadRubrics()
                } else {
                    toast.error(res.error || 'Erro ao atualizar rubrica')
                }
            } else {
                const res = await createRubric(data)
                if (res.success) {
                    toast.success('Rubrica criada com sucesso')
                    closeModal()
                    loadRubrics()
                } else {
                    toast.error(res.error || 'Erro ao criar rubrica')
                }
            }
        } catch {
            toast.error('Erro ao salvar rubrica')
        } finally {
            setSaving(false)
        }
    }

    const handleDelete = async () => {
        if (!deleteId) return
        setDeleting(true)
        try {
            const res = await deleteRubric(deleteId)
            if (res.success) {
                toast.success('Rubrica excluída com sucesso')
                setDeleteId(null)
                loadRubrics()
            } else {
                toast.error(res.error || 'Erro ao excluir rubrica')
            }
        } catch {
            toast.error('Erro ao excluir rubrica')
        } finally {
            setDeleting(false)
        }
    }

    if (loading) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
                <div className="loading loading-spinner loading-lg text-secondary"></div>
                <p className="text-base-content/60 text-sm">Carregando rubricas...</p>
            </div>
        )
    }

    return (
        <div className="p-4 md:p-6 max-w-7xl mx-auto">
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-secondary to-secondary/70 flex items-center justify-center shadow-lg">
                        <MdList className="text-2xl text-white" />
                    </div>
                    <div>
                        <h1 className="sm:text-lg lg:text-2xl font-bold text-base-content">
                            Rubricas
                        </h1>
                        <p className="text-sm text-base-content/60">
                            {rubrics.length} {rubrics.length === 1 ? 'rubrica' : 'rubricas'}
                        </p>
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    {canCreate && (
                        <button
                            className="btn btn-outline btn-sm gap-2"
                            onClick={handleSeed}
                            disabled={seeding}
                        >
                            {seeding ? (
                                <span className="loading loading-spinner loading-xs"></span>
                            ) : (
                                <MdPlaylistAdd className="text-lg" />
                            )}
                            Seed Padrão
                        </button>
                    )}
                    {canCreate && (
                        <button className="btn btn-secondary gap-2" onClick={openCreateModal}>
                            <MdAdd className="text-xl" />
                            <span className="hidden sm:inline">Nova Rubrica</span>
                        </button>
                    )}
                </div>
            </div>

            {/* Table */}
            {rubrics.length === 0 ? (
                <div className="flex flex-col items-center justify-center min-h-[400px] gap-4 bg-base-100 rounded-xl border border-base-300/50 p-8">
                    <div className="w-20 h-20 rounded-full bg-base-200 flex items-center justify-center">
                        <MdList className="text-4xl text-base-content/30" />
                    </div>
                    <div className="text-center">
                        <h3 className="text-lg font-semibold text-base-content mb-1">
                            Nenhuma rubrica cadastrada
                        </h3>
                        <p className="text-sm text-base-content/60 mb-4">
                            Crie rubricas manualmente ou utilize o seed padrão
                        </p>
                    </div>
                </div>
            ) : (
                <div className="overflow-x-auto bg-base-100 rounded-xl border border-base-300/50">
                    <table className="table">
                        <thead>
                            <tr>
                                <th>Código</th>
                                <th>Nome</th>
                                <th>Tipo</th>
                                <th>Cálculo</th>
                                <th className="text-right">Valor Padrão</th>
                                <th>Sistema</th>
                                <th>Ativo</th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {rubrics.map(rubric => (
                                <tr key={rubric.id} className="hover">
                                    <td className="font-mono text-sm font-semibold">{rubric.code}</td>
                                    <td>{rubric.name}</td>
                                    <td>
                                        <span className={`badge ${TYPE_BADGES[rubric.type] || 'badge-ghost'}`}>
                                            {TYPE_LABELS[rubric.type] || rubric.type}
                                        </span>
                                    </td>
                                    <td>{CALC_TYPE_LABELS[rubric.calculationType] || rubric.calculationType}</td>
                                    <td className="text-right">
                                        {rubric.defaultValue != null ? rubric.defaultValue : '-'}
                                    </td>
                                    <td>
                                        {rubric.isSystem && (
                                            <span className="badge badge-warning badge-sm">Sistema</span>
                                        )}
                                    </td>
                                    <td>
                                        {rubric.active ? (
                                            <span className="badge badge-success badge-sm">Ativo</span>
                                        ) : (
                                            <span className="badge badge-ghost badge-sm">Inativo</span>
                                        )}
                                    </td>
                                    <td>
                                        <div className="flex items-center gap-1 justify-end">
                                            {canUpdate && (
                                                <button
                                                    onClick={() => openEditModal(rubric)}
                                                    className="btn btn-ghost btn-xs"
                                                    title="Editar"
                                                >
                                                    <MdEdit className="text-lg" />
                                                </button>
                                            )}
                                            {canDelete && !rubric.isSystem && (
                                                <button
                                                    onClick={() => setDeleteId(rubric.id)}
                                                    className="btn btn-ghost btn-xs text-error"
                                                    title="Excluir"
                                                >
                                                    <MdDelete className="text-lg" />
                                                </button>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {/* Create/Edit Modal */}
            <dialog ref={modalRef} className="modal">
                <div className="modal-box !max-w-lg">
                    <h3 className="font-bold text-lg mb-4">
                        {editingId ? 'Editar Rubrica' : 'Nova Rubrica'}
                    </h3>

                    <div className="space-y-4">
                        <div className="grid grid-cols-2 gap-4">
                            <div className="form-control w-full">
                                <label className="label">
                                    <span className="label-text font-medium">Código *</span>
                                </label>
                                <input
                                    type="text"
                                    className="input input-bordered w-full font-mono"
                                    placeholder="Ex: 1001"
                                    value={form.code}
                                    onChange={e => setForm(prev => ({ ...prev, code: e.target.value }))}
                                    disabled={editingSystem}
                                />
                            </div>

                            <div className="form-control w-full">
                                <label className="label">
                                    <span className="label-text font-medium">Tipo *</span>
                                </label>
                                <select
                                    className="select select-bordered w-full"
                                    value={form.type}
                                    onChange={e => setForm(prev => ({ ...prev, type: e.target.value }))}
                                    disabled={editingSystem}
                                >
                                    {Object.entries(TYPE_LABELS).map(([key, label]) => (
                                        <option key={key} value={key}>{label}</option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        <div className="form-control w-full">
                            <label className="label">
                                <span className="label-text font-medium">Nome *</span>
                            </label>
                            <input
                                type="text"
                                className="input input-bordered w-full"
                                placeholder="Ex: Salário Base"
                                value={form.name}
                                onChange={e => setForm(prev => ({ ...prev, name: e.target.value }))}
                                disabled={editingSystem}
                            />
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                            <div className="form-control w-full">
                                <label className="label">
                                    <span className="label-text font-medium">Tipo de Cálculo</span>
                                </label>
                                <select
                                    className="select select-bordered w-full"
                                    value={form.calculationType}
                                    onChange={e => setForm(prev => ({ ...prev, calculationType: e.target.value }))}
                                >
                                    {Object.entries(CALC_TYPE_LABELS).map(([key, label]) => (
                                        <option key={key} value={key}>{label}</option>
                                    ))}
                                </select>
                            </div>

                            <div className="form-control w-full">
                                <label className="label">
                                    <span className="label-text font-medium">Valor Padrão</span>
                                </label>
                                <input
                                    type="number"
                                    step="0.01"
                                    className="input input-bordered w-full"
                                    placeholder="0.00"
                                    value={form.defaultValue}
                                    onChange={e => setForm(prev => ({ ...prev, defaultValue: e.target.value }))}
                                />
                            </div>
                        </div>

                        {form.calculationType === 'FORMULA' && (
                            <div className="form-control w-full">
                                <label className="label">
                                    <span className="label-text font-medium">Fórmula</span>
                                </label>
                                <input
                                    type="text"
                                    className="input input-bordered w-full font-mono"
                                    placeholder="Ex: BASE * 0.08"
                                    value={form.formula}
                                    onChange={e => setForm(prev => ({ ...prev, formula: e.target.value }))}
                                />
                            </div>
                        )}

                        <div className="form-control">
                            <label className="label">
                                <span className="label-text font-medium">Incidências</span>
                            </label>
                            <div className="grid grid-cols-2 gap-3 p-3 bg-base-200/50 rounded-lg">
                                <label className="label cursor-pointer justify-start gap-3">
                                    <input
                                        type="checkbox"
                                        className="checkbox checkbox-sm checkbox-secondary"
                                        checked={form.incidenceInss}
                                        onChange={e => setForm(prev => ({ ...prev, incidenceInss: e.target.checked }))}
                                    />
                                    <span className="label-text">INSS</span>
                                </label>
                                <label className="label cursor-pointer justify-start gap-3">
                                    <input
                                        type="checkbox"
                                        className="checkbox checkbox-sm checkbox-secondary"
                                        checked={form.incidenceIrrf}
                                        onChange={e => setForm(prev => ({ ...prev, incidenceIrrf: e.target.checked }))}
                                    />
                                    <span className="label-text">IRRF</span>
                                </label>
                                <label className="label cursor-pointer justify-start gap-3">
                                    <input
                                        type="checkbox"
                                        className="checkbox checkbox-sm checkbox-secondary"
                                        checked={form.incidenceFgts}
                                        onChange={e => setForm(prev => ({ ...prev, incidenceFgts: e.target.checked }))}
                                    />
                                    <span className="label-text">FGTS</span>
                                </label>
                                <label className="label cursor-pointer justify-start gap-3">
                                    <input
                                        type="checkbox"
                                        className="checkbox checkbox-sm checkbox-secondary"
                                        checked={form.incidenceVacation13}
                                        onChange={e => setForm(prev => ({ ...prev, incidenceVacation13: e.target.checked }))}
                                    />
                                    <span className="label-text">Férias/13o</span>
                                </label>
                            </div>
                        </div>

                        <div className="form-control">
                            <label className="label cursor-pointer justify-start gap-3">
                                <input
                                    type="checkbox"
                                    className="toggle toggle-secondary toggle-sm"
                                    checked={form.active}
                                    onChange={e => setForm(prev => ({ ...prev, active: e.target.checked }))}
                                />
                                <span className="label-text">Ativo</span>
                            </label>
                        </div>
                    </div>

                    <div className="modal-action">
                        <button className="btn btn-ghost" onClick={closeModal} disabled={saving}>
                            Cancelar
                        </button>
                        <button className="btn btn-secondary" onClick={handleSave} disabled={saving}>
                            {saving && <span className="loading loading-spinner loading-sm"></span>}
                            {editingId ? 'Salvar' : 'Criar'}
                        </button>
                    </div>
                </div>
                <form method="dialog" className="modal-backdrop">
                    <button onClick={closeModal}>close</button>
                </form>
            </dialog>

            {/* Delete confirmation */}
            <ConfirmModal
                isOpen={!!deleteId}
                onClose={() => setDeleteId(null)}
                onConfirm={handleDelete}
                title="Excluir Rubrica"
                message="Tem certeza de que deseja excluir esta rubrica? Esta ação não pode ser desfeita."
                loading={deleting}
                confirmButtonText="Excluir"
                cancelButtonText="Cancelar"
            />
        </div>
    )
}
